/**
 * React Query Configuration
 * Sets up the QueryClient with default options for the entire app
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: How long data is considered "fresh" (no refetch)
      staleTime: 5 * 60 * 1000, // 5 minutes

      // Cache time: How long unused data stays in cache
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime in v4)

      // Retry failed requests
      retry: 1,

      // Refetch on window focus so out-of-band changes (Booqable webhook
      // upserts, edits in another tab) appear when the user tabs back in.
      // `true` (the default) only refetches when the query is stale, so it
      // honors the per-query staleTimes below — a quick tab-switch on fresh
      // data won't re-fire. (`'always'` would refetch regardless of staleTime,
      // firing every active query on every focus event.)
      refetchOnWindowFocus: true,

      // Refetch on mount if data is stale
      refetchOnMount: true,

      // Refetch on network reconnect
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry failed mutations
      retry: 0,
    },
  },
});

/**
 * Query Keys
 * Centralized query key definitions for type safety and consistency
 */
export const queryKeys = {
  customers: {
    all: ['customers'] as const,
    detail: (id: string) => ['customers', id] as const,
    locations: (id: string) => ['customers', id, 'locations'] as const,
    orders: (id: string) => ['customers', id, 'orders'] as const,
    contacts: (id: string) => ['customers', id, 'contacts'] as const,
    savedCards: (id: string) => ['customers', id, 'saved-cards'] as const,
    authnetReview: ['customers', 'authnet-review'] as const,
  },
  documents: {
    all: ['documents'] as const,
    byType: (type: string) => ['documents', type] as const,
  },
  orders: {
    all: ['orders'] as const,
    concept: ['orders', 'concept'] as const,
    detail: (id: string) => ['orders', id] as const,
    // The full local Supabase mirror row for one order (GET /orders/:id, raw
    // `{ data }` envelope). Shared by the OrderDetails load effect, the
    // right-sidebar waive-fee gate, and the customer-notes placement/ack gate
    // so the row is fetched ONCE per open instead of three times under three
    // different keys.
    localRow: (id: string) => ['order-local', id] as const,
  },
  projects: {
    all: ['projects'] as const,
    detail: (id: string) => ['projects', id] as const,
  },
  inventory: {
    all: ['inventory'] as const,
    detail: (id: string) => ['inventory', id] as const,
  },
  users: {
    all: ['users'] as const,
    detail: (id: string) => ['users', id] as const,
  },
  payments: {
    // In-flight ACH debits worklist (/payments/ach/unsettled).
    achUnsettled: ['payments', 'ach-unsettled'] as const,
  },
  emailTemplates: {
    // Keyed by the order context so a different order re-fetches the
    // server-rendered (real-variable) subject/body. `null` = no order context.
    list: (orderId: string | null | undefined) =>
      ['email-templates', orderId ?? null] as const,
  },
};
